import {
  AgGridColFactoryService,
  PPSSTabName,
} from '@products/fund-listing/services/ag-grid-col-factory.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ConfigurationId,
  FundShareClassId,
  PPSSState,
  ProductData,
} from '@types';
import { Subject, Subscription } from 'rxjs';

import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { ColDef } from '@ag-grid-community/core';
import { FundNameCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-name-cell-renderer/fund-name-cell-renderer.component';
import { PpssCardContainerComponent } from '@products/fund-listing/ppss-card-container/ppss-card-container.component';
import { PpssService } from '../../services/ppss.service';
import { PpssTableBase } from '../../ppss-table-base';
import { TranslateService } from '@shared/translate/translate.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PpssServiceFactory } from '@products/fund-listing/services/ppss.service.factory';

@Component({
  selector: 'ft-ppss-fundIdentifiers',
  templateUrl: './ppss-fundIdentifiers.component.html',
  styleUrls: ['./ppss-fundIdentifiers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpssFundidentifiersComponent
  extends PpssTableBase
  implements OnInit, OnChanges, OnDestroy {
  @Input() isHandheld = false;
  @Input() active;
  // to decidde the number of rows in ag-grid
  isSiteIntl: boolean;
  productType: ConfigurationId;
  currentPage: ProductData[];
  private ppssSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();
  // AG-Grid specific variables.
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    // cellClass: ['ag-cell-frk--highlightedTertiary'],
    // flex: 1,
  };
  gridContext = {
    filteredRows: [],
  };
  frameworkComponents = {
    fundNameCellRendererComponent: FundNameCellRendererComponent,
  };
  numberofFilteredData: number;

  @ViewChild('fundIdentifiersGrid', { static: false })
  fundIdentifiersGrid: AgGridAngular;
  @ViewChild('fundIdentifierCards', { static: false })
  fundIdentifierCards: PpssCardContainerComponent;
  private ppssService: PpssService;

  constructor(
    private cdr: ChangeDetectorRef,
    ppssServiceFactory: PpssServiceFactory,
    private translateService: TranslateService,
    private agGridHelperService: AgGridHelperService,
    private agGridColFactoryService: AgGridColFactoryService
  ) {
    super();
    this.ppssService = ppssServiceFactory.getPpssService();
    this.productType = this.ppssService.getProductType();
  }
  ngOnInit() {
    // ***** NGC-12033: Tooltip didn't invoke *****
    // Removed for now, but was it required? FIX-ME!
    // this.cdr.detach();
    const state = this.ppssService.getState();
    if (state) {
      this.setColumnVisibility(state);
    }
    this.ppssSubscription = this.ppssService.subscribe(
      (ppssState: PPSSState) => {
        this.mapState(ppssState);
      }
    );
    this.handlePagination();
    this.isPopulated = true;
  }

  /**
   * Handling Pagination.
   */
  private handlePagination() {
    this.ppssService.getNumberOfPages$().subscribe(() => {
      this.fundIdentifiersGrid?.api?.paginationSetPageSize(
        Number(this.ppssService.getPageSize())
      );
      this.cdr.detectChanges();
    });
    this.ppssService
      .getCurrentPage$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pageData: ProductData[]): void => {
        this.currentPage = pageData;
        this.cdr.detectChanges();
      });
    this.ppssService
      .getPageNumber$()
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((n: number): void => {
        this.fundIdentifiersGrid?.api.paginationGoToPage(n - 1);

        if (!this.ppssService._isFilterProcessing) {
          const firstRowIndex =
            this.fundIdentifiersGrid?.api.paginationGetPageSize() * (n - 1);
          this.fundIdentifiersGrid?.api.setFocusedCell(
            firstRowIndex,
            'FUND-NAME'
          );
        }
        this.redrawGrid(this);
      });
  }
  redrawGrid(ref) {
    ref.fundIdentifiersGrid?.api.redrawRows();
  }
  ngOnDestroy() {
    this.ppssSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    // need to explicitly run detectChanges() when isHandheld changes, because component has been detached from change detection earlier
    if (changes.isHandheld) {
      this.cdr.detectChanges();
    }
    // if (this.active) {
    //   const that = this;
    //   setTimeout(() => {
    //     that.redrawGrid(that);
    //   }, 1000);
    // }
  }

  /**
   * Called when the state changes.
   * @param state initial/refreshed state.
   */
  mapState(state: PPSSState) {
    if (state.lastAction === 'populate' || this.firstTime) {
      this.onPopulate(state);
    }
    if (state.lastAction === 'filterRows' || this.firstTime) {
      this.numberofFilteredData = state.data.filteredTableRows.length;
      this.cdr.detectChanges();
      this.gridContext.filteredRows = state.data.filteredTableRows;
      this.fundIdentifiersGrid?.api.onFilterChanged();
      this.fundIdentifiersGrid?.api.resetRowHeights();
    }

    if (state.lastAction === 'updateRows') {
      this.fundIdentifiersGrid?.api.resetRowHeights();
    }

    this.firstTime = false;
  }

  /**
   * Called on initial population of the data and config.
   * Set config details and define column defs.
   * @param state initial state.
   */
  onPopulate(state: PPSSState) {
    super.onPopulate(state);
    if (!this.isPopulated) {
      return;
    }
    this.cdr.detectChanges();
  }

  setColumnVisibility(state: PPSSState) {
    const colId = [];

    // Fetch all columns from state.
    state.config.identifierList.forEach((col) => colId.push(col));

    // Fetch identifiers col def from factory.
    const identifiersColDefs: ColDef[] = this.agGridColFactoryService.generateColDefs(
      PPSSTabName.IDENTIFIERS,
      this,
      state,
      this.agGridHelperService,
      this.translateService
    );

    // Push the fund name as the first column in the list.
    this.columnDefs.push(
      identifiersColDefs.find((colDefObj) => colDefObj.colId.match('FUND-NAME'))
    );

    // Traverse fund identifiers colDef list and push it to ColDef array as per configuration.
    colId.forEach((col) => {
      const colDef = identifiersColDefs.find(
        (colDefObj) => colDefObj.colId === col
      );

      if (colDef) {
        this.columnDefs.push(colDef);
      }
    });
  }

  updateFavourite(fund: FundShareClassId) {
    this.ppssService.updateFavourite(fund);
  }

  isExternalFilterPresent = () => true;

  doesExternalFilterPass(node: any) {
    const filteredRows = node.gridOptionsWrapper.getContext().filteredRows;
    if (filteredRows) {
      return filteredRows.indexOf(node.data) > -1;
    }
    return true;
  }

  // change detection on sorting
  onSortChanged = () => this.cdr.detectChanges();
}
